import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import momentTZ from 'moment-timezone';

// Extend dayjs with utc and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const systemTimezone = momentTZ.tz.guess();
import '../features/schedule/scheduleApi';
import '../features/prediction/predictionApi';

const isNullOrUndefined = (item: string | number | [] | object | undefined | null): boolean =>
    item === null || item === undefined;

// eslint-disable-next-line @typescript-eslint/naming-convention
const ENDPOINT_MAP: Record<string, Array<{ key: string; endpoint: string }>> = {
    settings: [{ key: 'settings', endpoint: 'getSettings' }],
    details: [{ key: 'details', endpoint: 'getChannelDetails' }],
    properties: [{ key: 'properties', endpoint: 'getChannelDetails' }],
    gauge: [{ key: 'gauge', endpoint: 'getChannelDetails' }],
    /* eslint-disable @typescript-eslint/naming-convention */
    device_list: [{ key: 'device_list', endpoint: 'getAllDevices' }],
    timeline: [
        { key: 'timeline', endpoint: 'getDeviceTimeline' },
        { key: 'timeline', endpoint: 'getDeviceAlertCount' },
    ],
    trends: [{ key: 'trends', endpoint: 'getDeviceTrends' }],
    group_list: [{ key: 'group_list', endpoint: 'getAllFlattenedGroups' }],
    map: [{ key: 'map', endpoint: 'deviceMap' }],
    loads: [{ key: 'loads', endpoint: 'getMultiDeviceLoads' }],
    load_group_trends: [
        { key: 'load_group_trends', endpoint: 'getLoadGroupsListAll' },
        { key: 'load_group_trends', endpoint: 'getLoadGroupTrends' },
    ],
    load_groups: [{ key: 'load_groups', endpoint: 'getLoadGroupsListAll' }],
    virtual_loads: [{ key: 'virtual_loads', endpoint: 'getAllVirtualLoads' }],
    consumption_breakdown: [{ key: 'consumption_breakdown', endpoint: 'getConsumptionBreakdownData' }],
    load_trends: [{ key: 'load_trends', endpoint: 'getLoadTrendsData' }],
    energy_flow: [{ key: 'energy_flow', endpoint: 'getEnergyFLowAssetsData' }],
    metrics: [{ key: 'metrics', endpoint: 'getMetricsWidgetData' }],
    upcoming: [{ key: 'upcoming', endpoint: 'getPredictionFilters' }],
    environmental_stress_monitoring: [
        { key: 'environmental_stress_monitoring', endpoint: 'getEnvironmentalStressMonitoringSummary' },
        { key: 'environmental_stress_monitoring', endpoint: 'getEnvironmentalStressMonitoringPreview' },
    ],
    score: [{ key: 'score', endpoint: 'getScore' }],
    components: [{ key: 'components', endpoint: 'getComponentFilter' }],
    weather: [{ key: 'weather', endpoint: 'getWeatherData' }],
    schedule: [{ key: 'schedule', endpoint: 'getAllSchedules' }],
    command_bar: [
        { key: 'command_bar', endpoint: 'getChannelDetails' },
        { key: 'command_bar', endpoint: 'getAvailableCommands' },
    ],
    /* eslint-enable @typescript-eslint/naming-convention */
};

function customEndpointSelector(type: string, additionalAggregatedKey?: any): Array<{ key: string; endpoint: string }> {
    if (type === 'aggregated_trends') {
        if (additionalAggregatedKey === 'line') return [{ key: type, endpoint: 'getAggregatedLineChartData' }];
        if (additionalAggregatedKey === 'CSV') return [{ key: type, endpoint: 'downloadChartData' }];
        return [{ key: type, endpoint: 'getAggregatedBarChartData' }];
    }
    return ENDPOINT_MAP[type] ?? [{ key: type, endpoint: 'getChannelDetails' }];
}

function getMqttTopiceByWidgetType(type: string): string {
    if (type === 'trends') return 'BSSRM/TREND';
    if (type === 'command_bar') return 'BSSRM/COMMAND_TOPIC';
    return 'BSSRM';
}

const SKELETON_AND_SOURCE_TYPES = ['settings', 'properties', 'loads', 'load_groups', 'components'];
const SKELETON_PRIMARY_TYPES = ['timeline', 'schedule', 'upcoming', 'environmental_stress_monitoring'];
const SKELETON_ONLY_TYPES = ['group_list', 'device_list', 'virtual_loads'];
const SOURCE_ONLY_TYPES = ['gauge', 'charts', 'load_group_trends', 'trends', 'command_bar'];

function getWidgetTypeValidationConfig(type: string): { [key: string]: string | boolean | number } {
    const requiredConfig: { [x: string]: string | boolean | number } = {
        isSourceRequired: false,
        loaderType: 'progress',
    };

    if (type === 'details') {
        requiredConfig['primarySkeletonCount'] = 3;
        requiredConfig['loaderType'] = 'skeleton';
        requiredConfig['isSourceRequired'] = true;
    } else if (type === 'score') {
        requiredConfig['selectionType'] = 'radio';
    } else if (SKELETON_AND_SOURCE_TYPES.includes(type)) {
        requiredConfig['loaderType'] = 'skeleton';
        requiredConfig['isSourceRequired'] = true;
    } else if (SKELETON_PRIMARY_TYPES.includes(type)) {
        requiredConfig['loaderType'] = 'skeleton';
        requiredConfig['primarySkeletonCount'] = 4;
    } else if (SKELETON_ONLY_TYPES.includes(type)) {
        requiredConfig['loaderType'] = 'skeleton';
    } else if (SOURCE_ONLY_TYPES.includes(type)) {
        requiredConfig['isSourceRequired'] = true;
    }

    return requiredConfig;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const CHANNEL_TYPE_MAP: Record<string, string> = {
    details: 'DETAIL',
    settings: 'SETTING',
    properties: 'PROPERTIES',
    loads: 'LOAD',
    trends: 'TREND',
    gauge: 'TREND',
    charts: 'TREND',
    /* eslint-disable @typescript-eslint/naming-convention */
    command_bar: 'CONTROL',
    /* eslint-enable @typescript-eslint/naming-convention */
};

function retrieveChannelTypefromWidgetType(widgetType?: string): string {
    return (widgetType && CHANNEL_TYPE_MAP[widgetType]) ?? 'DETAIL';
}

function isTrendsApplicableForDrop(config: any, isMultiDevice: any, dropType: any): boolean {
    if (dropType !== 'GROUP') return true;
    const trendsConfig = config?.config;
    const effectiveMultiDevice = trendsConfig ? trendsConfig.multiDevice : isMultiDevice;
    return Boolean(effectiveMultiDevice);
}

function isWidgetApplicable(widgetData: any, dropType: any): boolean {
    const id = widgetData?.widgetType?.id;
    const config = widgetData?.config;
    const isMultiDevice = config?.multiDevice;

    if (id === 'device_list' || id === 'group_list') {
        return isMultiDevice || dropType !== 'DEVICE';
    }

    if (['charts', 'details', 'settings', 'properties', 'loads', 'gauge', 'command_bar'].includes(id)) {
        return isMultiDevice || dropType !== 'GROUP';
    }

    if (id === 'trends') {
        return isTrendsApplicableForDrop(config, isMultiDevice, dropType);
    }

    return true;
}

function isWidgetConfiguredLoads(id: string, config: any): boolean {
    const singleDeviceConfigured = !config?.multiDevice && config?.secondary?.length;
    const multiDeviceConfigured = config?.multiDevice && config?.selectedNodes?.length;
    return Boolean(config?.isAutomated || singleDeviceConfigured || multiDeviceConfigured);
}

const SECONDARY_CONFIG_WIDGETS = ['properties', 'settings', 'command_bar', 'charts'];
const FILTER_BASED_WIDGETS = [
    'group_list',
    'device_list',
    'timeline',
    'schedule',
    'map',
    'environmental_stress_monitoring',
];

const isTrendWidgetConfigured = (config: any): boolean =>
    config?.config ? Boolean(config?.config?.channelData?.length) : Boolean(config?.channelData?.length);

const isFilterBasedWidgetConfigured = (config: any): boolean =>
    !config?.multiDevice || Boolean(Object.keys(config?.filters ?? {})?.length);

const WIDGET_CONFIG_EVALUATORS: Record<string, (config: any) => boolean> = {
    details: (config: any): boolean => Boolean(config?.secondary?.length || config?.primary?.length),
    trends: isTrendWidgetConfigured,
    loads: (config: any): boolean => isWidgetConfiguredLoads('loads', config),
    // Gauge without a resolved channelId (e.g. instance-profile-filtered device with no matching channel)
    // must render the "Not Configured" card, otherwise retained min/max would draw an empty scale.
    gauge: (config: any): boolean => Boolean(config?.data?.channelId),
};

WIDGET_CONFIG_EVALUATORS['load_group_trends'] = (config: any): boolean =>
    isWidgetConfiguredLoads('load_group_trends', config);
WIDGET_CONFIG_EVALUATORS['load_groups'] = (): boolean => true;

FILTER_BASED_WIDGETS.forEach((widgetType) => {
    WIDGET_CONFIG_EVALUATORS[widgetType] = isFilterBasedWidgetConfigured;
});

function isWidgetConfigured(widgetData: any): boolean {
    const id = widgetData?.widgetType?.id;
    const config = widgetData?.config;

    if (SECONDARY_CONFIG_WIDGETS.includes(id)) {
        return Boolean(config?.secondary?.length);
    }

    const evaluator = WIDGET_CONFIG_EVALUATORS[id];
    return evaluator ? evaluator(config) : true;
}

function getRandomColor(): string {
    const letters: any = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
}

function timeStampHandler(
    timeZone: any,
    weekStart: any,
    selection: any,
    customFromTimestamp?: any,
    customToTimestamp?: any,
    widgetType?: any
): { from: number; to: number; prevFrom: number; prevTo: number } {
    const now = momentTZ.tz(timeZone);

    const dayOfWeek = now.day();
    let diffToStartOfWeek: number;
    if (weekStart?.toLowerCase() === 'monday') {
        diffToStartOfWeek = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    } else {
        diffToStartOfWeek = -dayOfWeek;
    }
    let start: any;
    let prevStart: any;
    let prevEnd: any;

    switch (selection) {
        case 'today':
            start = now.startOf('day');
            if (widgetType === 'load_trends') {
                prevStart = now.subtract(7, 'day').startOf('day');
                prevEnd = now.subtract(6, 'day').startOf('day');
            } else {
                prevStart = now.subtract(1, 'day').startOf('day');
                prevEnd = start;
            }
            break;
        case 'week':
            start = now.add(diffToStartOfWeek, 'day').startOf('day');
            prevStart = now.add(diffToStartOfWeek - 7, 'day').startOf('day');
            prevEnd = start;
            break;
        case 'month':
            start = now.startOf('month');
            prevStart = now.subtract(1, 'month').startOf('month');
            prevEnd = start;
            break;
        case 'year':
            start = now.startOf('year');
            prevStart = now.subtract(1, 'year').startOf('year');
            prevEnd = start;
            break;
        case 'custom':
            return {
                from: dayjs(customFromTimestamp).tz(timeZone).valueOf(),
                to: dayjs(customToTimestamp).tz(timeZone).valueOf(),
                prevFrom: dayjs(customFromTimestamp)
                    .subtract(customToTimestamp - customFromTimestamp + 1, 'millisecond')
                    .tz(timeZone)
                    .valueOf(),
                prevTo: dayjs(customFromTimestamp).subtract(1, 'millisecond').tz(timeZone).valueOf(),
            };
        default:
            throw new Error("Invalid type provided. Use 'today', 'week', 'month', or 'year'.");
    }

    return {
        from: start.valueOf(), // start of the period in the specified timezone
        to: momentTZ.tz(timeZone).valueOf(), // ensure 'to' is recalculated in the specified timezone
        prevFrom: prevStart.valueOf(), // start of the previous period in the specified timezone
        prevTo: prevEnd.valueOf() - 1, // end of the previous period in the specified timezone
    };
}

const formatTime = (timeData: any): string => {
    let timeStr = '';
    if (timeData?.hours > 0) {
        timeStr += `${timeData?.hours}h`;
    }
    if (timeData?.minutes > 0 || timeData?.hours > 0) {
        timeStr += timeData?.minutes < 10 ? `0${timeData?.minutes}m` : `${timeData?.minutes}m`;
    }
    return (timeStr += timeData?.seconds < 10 ? `0${timeData?.seconds}s` : `${timeData?.seconds}s`);
};
const getTimeInFromTo = (value: number, key: string): any => {
    const current: number = Date.now();
    let newValue = 0;
    if (key === 'h') {
        newValue = value * 60 * 60 * 1000;
    }
    return {
        from: current - newValue,
        to: current,
    };
};

const getActiveItemColor = (severity: any, isAcknowledged: any, isDetails?: boolean): string | null => {
    if ((severity === 'Alarm' || isDetails) && severity !== 'Offline' && !isAcknowledged) {
        return '#CA3C3D';
    }
    if (severity === 'Offline') {
        return '#69B1C3';
    }
    if (isAcknowledged) {
        return '#E0F1FD';
    }
    return null;
};

const getListItemBackgroundColor = (data: any, isDetails?: boolean): string => {
    const status = data?.status;
    const severity = data?.severity;
    const isAcknowledged = data?.isAcknowledged;

    if (status === 'Active') {
        const color = getActiveItemColor(severity, isAcknowledged, isDetails);
        if (color !== null) {
            return color;
        }
    }
    if (severity === 'Alarm' && isAcknowledged && data?.isCleared) {
        return '#D5D8DA';
    }
    return 'transparent';
};

const getListItemTextColor = (data: any): string => {
    if (data?.status === 'Cleared' || data?.severity === 'Cleared') {
        return '#424E54';
    } else if (data?.severity === 'Alarm' && data?.isAcknowledged) {
        return '#0088F2';
    } else if (data?.status === 'Active' && data?.severity === 'Offline') {
        return '#DBEEF2';
    }
    return '#FFFFFF';
};

const getUTCTimestamp = (timestamp: number): number => timestamp;

const TOGGLE_FILTERS = ['today', 'week', 'month', 'year'];

const toCamelCaseAndCapitalize = (str: any): string => {
    const s =
        str &&
        str
            .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
            .map((x: string) => x.slice(0, 1).toUpperCase() + x.slice(1).toLowerCase())
            .join('');
    return s.slice(0, 1).toUpperCase() + s.slice(1);
};

function getFromToFormat(type: string, selectedDate: any): any {
    if (type === 'week') {
        return {
            from: dayjs(selectedDate)?.clone()?.startOf('week').valueOf(),
            to: dayjs(selectedDate)?.clone()?.endOf('week').valueOf(),
        };
    }
    return {
        to: new Date(selectedDate).setHours(23, 59, 59, 0),
        from: new Date(selectedDate)?.setHours(0, 0, 0, 0),
    };
}

const getActiveAlarmLabel = (severity: any, isAcknowledged: any): string | null => {
    if (severity === 'Offline') {
        return severity?.toLocaleUpperCase();
    }
    if (isAcknowledged) {
        return 'ACKNOWLEDGED';
    }
    if (severity === 'Quarantined') {
        return '';
    }
    return null;
};

const getAlarmLabelValue = (data: any): string => {
    const status = data?.status;
    const severity = data?.severity;
    const isAcknowledged = data?.isAcknowledged;

    if (status === 'Active') {
        const label = getActiveAlarmLabel(severity, isAcknowledged);
        if (label !== null) {
            return label;
        }
    }
    return isAcknowledged && data?.isCleared ? 'CLOSED' : status?.toLocaleUpperCase?.();
};

const transformScheduleWidgetData = (scheduleWidgetData: any): any => {
    return scheduleWidgetData?.map((scheduleItem: any) => {
        return {
            id: scheduleItem.id,
            scheduleDate: new Date(scheduleItem?.schedules?.[0]).setHours(0, 0, 0, 0),
            schedules: [
                {
                    startTime: scheduleItem?.schedules?.[0],
                    ...(scheduleItem.actionEndTime && {
                        endTime: new Date(scheduleItem?.schedules?.[0]).setHours(
                            new Date(scheduleItem.actionEndTime).getHours(),
                            new Date(scheduleItem.actionEndTime).getMinutes(),
                            new Date(scheduleItem.actionEndTime).getSeconds()
                        ),
                    }),
                    name: scheduleItem?.name,
                    deviceCount: scheduleItem?.deviceCount,
                },
            ],
        };
    });
};

const CHART_TYPE: any = {
    bar: 'BAR_CHART',
    pie: 'PIE_CHART',
    column: 'COLUMN_CHART',
    line: 'LINE_CHART',
    area: 'AREA_CHART',
};

const getChartMaxValue = (newData: any): any => {
    const sortedData = (a: any, b: any): any => {
        const aVal = a?.value ? +a.value : 0;
        const bVal = b?.value ? +b.value : 0;
        if (aVal > bVal) return -1;
        if (aVal < bVal) return 1;
        return 0;
    };
    return newData?.sort?.(sortedData)[0];
};

const getIntervalValue = (dataValue: any): any => {
    let value = 0;
    if (isNaN(dataValue)) value = getChartMaxValue(dataValue)?.value;
    else value = dataValue ?? 0;
    if (value > 10) return Math.ceil(value / 10);
    return value ? (value / 12)?.toFixed?.(2) : undefined;
};

const fixEtcGMT = (profileTimezone: string): string => {
    const match = profileTimezone.match(/^Etc\/GMT([+-]\d+)$/);
    if (match) {
        const offset = parseInt(match[1], 10);
        return `Etc/GMT${offset > 0 ? `-${offset}` : `+${-offset}`}`;
    }
    return profileTimezone;
};

function getUniqueDeviceTypeIds(data: any): any {
    const result: any = [];
    if (!data?.length) {
        return result;
    }

    for (let i = 0; i < data?.length; i++) {
        const id = data[i].deviceTypeId;

        if (result.indexOf(id) === -1) {
            result.push(id);
        }
    }

    return result;
}

export {
    getRandomColor,
    isNullOrUndefined,
    customEndpointSelector,
    getMqttTopiceByWidgetType,
    retrieveChannelTypefromWidgetType,
    getFromToFormat,
    formatTime,
    timeStampHandler,
    systemTimezone,
    getTimeInFromTo,
    isWidgetConfigured,
    getListItemBackgroundColor,
    getListItemTextColor,
    TOGGLE_FILTERS,
    toCamelCaseAndCapitalize,
    getAlarmLabelValue,
    getWidgetTypeValidationConfig,
    isWidgetApplicable,
    transformScheduleWidgetData,
    CHART_TYPE,
    getIntervalValue,
    getChartMaxValue,
    getUTCTimestamp,
    fixEtcGMT,
    getUniqueDeviceTypeIds,
};
